/**
 * The `~` overlay.
 *
 * A key/value panel for the numbers the verification plan cares about — apex
 * height, substep count, ghost LOD split, interpolation mode. Hidden by default;
 * it is a diagnostic, not a feature.
 *
 * `set` is safe to call every frame: rows are created once, keyed by name, and a
 * value is written only when its text actually changes.
 */

import './ui.css';

interface Row {
  el: HTMLElement;
  value: HTMLElement;
  last: string;
}

export class DebugOverlay {
  private readonly root: HTMLElement;
  private readonly rows = new Map<string, Row>();

  constructor() {
    const mount = document.getElementById('ui-root') ?? document.body;
    this.root = document.createElement('div');
    this.root.className = 'sk-debug';
    this.root.hidden = true;
    mount.appendChild(this.root);
  }

  /** Currently on screen. */
  get visible(): boolean {
    return !this.root.hidden;
  }

  /** Flip the panel, or force a state with `force`. */
  toggle(force?: boolean): void {
    this.root.hidden = force === undefined ? !this.root.hidden : !force;
  }

  /**
   * Write a batch of values. Keys appear in first-seen order and persist, so the
   * panel does not reshuffle itself between frames as values come and go.
   */
  set(values: Record<string, string | number>): void {
    if (this.root.hidden) return; // invisible panel, zero cost
    for (const key in values) {
      const raw = values[key];
      if (raw === undefined) continue;
      const text = typeof raw === 'number' ? formatNumber(raw) : raw;
      let row = this.rows.get(key);
      if (row === undefined) {
        const el = document.createElement('div');
        el.className = 'sk-debug__row';
        const k = document.createElement('span');
        k.className = 'sk-debug__k';
        k.textContent = key;
        const v = document.createElement('span');
        v.className = 'sk-debug__v';
        el.append(k, v);
        this.root.appendChild(el);
        row = { el, value: v, last: '' };
        this.rows.set(key, row);
      }
      if (row.last !== text) {
        row.last = text;
        row.value.textContent = text;
      }
    }
  }

  /** Forget every row. Useful when switching what is being profiled. */
  clear(): void {
    this.rows.clear();
    this.root.replaceChildren();
  }

  dispose(): void {
    this.rows.clear();
    this.root.remove();
  }
}

/** Integers stay integers; everything else gets two decimals and no exponent. */
function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  return Number.isInteger(n) ? String(n) : n.toFixed(2);
}
